/**
 * Beisat ↔ apps bridge (Smart Wishlist, Selleasy bundles) + back buttons.
 * - [data-beisat-back]: go to the previous page on this store, else follow the link.
 * - Wishlist "Add to Cart": add in the background, keep the wishlist open, confirm on the
 *   button + toast, refresh the mini cart (the app used to submit a form and jump to /cart).
 * - Wishlist cards show the saved variant's size ("Size: M").
 * - Any add-to-cart made by an app script (cdn.shopify.com/extensions/…) refreshes the
 *   mini cart and shows a toast; a failed add (e.g. the only piece is already in the cart)
 *   shows the store's message instead of failing silently.
 */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const isAr = (document.documentElement.lang || '').toLowerCase().indexOf('ar') === 0;

  // Adds stay on the page: refresh the mini cart + header count and confirm with a
  // toast (customers found the cart popping open after every add annoying).
  const addedToCart = () => {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    notify(isAr ? 'تمت الإضافة إلى السلة' : 'Added to your cart');
  };

  const openMiniCart = () => {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    window.setTimeout(() => {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
    }, 350);
  };

  const notify = (message) => {
    if (!message) return;
    let el = document.getElementById('beisat-app-notice');
    if (!el) {
      el = document.createElement('div');
      el.id = 'beisat-app-notice';
      el.setAttribute('role', 'alert');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('is-visible');
    window.clearTimeout(el._t);
    el._t = window.setTimeout(() => el.classList.remove('is-visible'), 4000);
  };

  const errorText = async (res) => {
    try {
      const data = await res.clone().json();
      return data.description || data.message || '';
    } catch (e) {
      return '';
    }
  };

  /* Cart page "Edit": open the mini cart here; refresh the order list if the cart changed. */
  document.addEventListener('click', async (event) => {
    const edit = event.target.closest && event.target.closest('.advanced-cart-preview .card__edit');
    if (!edit) return;
    event.preventDefault();
    const sig = async () => {
      try {
        const c = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        return c.items.map((i) => i.key + ':' + i.quantity).join(',');
      } catch (e) {
        return '';
      }
    };
    const before = await sig();
    openMiniCart();
    // Wait for the drawer's own close event (the drawer element is re-rendered
    // when items change, so holding a reference to it is not reliable).
    document.addEventListener(
      'on:cart-drawer:before-close',
      async () => {
        if ((await sig()) !== before) location.reload();
      },
      { once: true }
    );
  });

  /* Currency app notice (Bucks) in Arabic on /ar. */
  const translateBucks = () => {
    if (!isAr) return;
    document.querySelectorAll('.buckscc-cart-banner .ess-cart-banner, .buckscc-cart-banner .hxoCartBanner').forEach((el) => {
      if (/process all orders/i.test(el.textContent)) {
        el.textContent = 'نقوم بمعالجة جميع الطلبات بالريال العماني، وسيتم الدفع بأحدث أسعار الصرف.';
        el.setAttribute('dir', 'rtl');
      }
    });
  };
  window.addEventListener('load', () => {
    translateBucks();
    window.setTimeout(translateBucks, 1500);
    window.setTimeout(translateBucks, 4000);
  });

  /* Back buttons */
  document.addEventListener('click', (event) => {
    const back = event.target.closest && event.target.closest('[data-beisat-back]');
    if (!back) return;
    let sameStore = false;
    try {
      sameStore = !!document.referrer && new URL(document.referrer).host === location.host;
    } catch (e) {}
    // "always": go back whenever there is history (cart page); otherwise only
    // when the previous page was on this store.
    const always = back.getAttribute('data-beisat-back') === 'always';
    if ((always || sameStore) && window.history.length > 1) {
      event.preventDefault();
      window.history.back();
    }
  });

  /* Wishlist Add to Cart → background add + mini cart */
  document.addEventListener(
    'click',
    async (event) => {
      const btn = event.target.closest && event.target.closest('.st-atc-button');
      if (!btn) return;
      const form = btn.closest('form');
      if (!form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      btn.disabled = true;
      try {
        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!res.ok) {
          notify(await errorText(res));
          return;
        }
        // Stay in the wishlist (client, 2026-11-01): confirm on the button and with the
        // toast (now above the panel), and refresh the cart behind it.
        addedToCart();
        const label = btn.textContent;
        btn.classList.add('is-added');
        btn.textContent = isAr ? '✓ تمت الإضافة' : '✓ Added';
        window.setTimeout(() => {
          btn.classList.remove('is-added');
          btn.textContent = label;
        }, 2000);
      } catch (e) {
        form.submit();
      } finally {
        btn.disabled = false;
      }
    },
    true
  );

  /* Wishlist cards: show the saved variant's size */
  const productCache = {};
  const loadProduct = (handle) => {
    if (!productCache[handle]) {
      productCache[handle] = fetch(root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    return productCache[handle];
  };
  const sizeNames = ['size', 'المقاس', 'مقاس', 'الحجم'];
  const sizeLabel = isAr ? 'المقاس' : 'Size';
  const annotateWishlist = () => {
    document.querySelectorAll('st-product-card:not([data-beisat-size])').forEach(async (card) => {
      card.setAttribute('data-beisat-size', '');
      const handle = card.getAttribute('handle') || card.dataset.handle;
      const variantId = String(card.getAttribute('variantid') || card.getAttribute('variant-id') || card.dataset.variantId || '');
      if (!handle) return;
      const product = await loadProduct(handle);
      if (!product || !product.variants || !product.variants.length) return;
      const variant = product.variants.find((v) => String(v.id) === variantId) || product.variants[0];
      const idx = (product.options || []).findIndex((o) => sizeNames.indexOf(String(o.name || o).toLowerCase()) !== -1);
      const value = idx >= 0 ? variant['option' + (idx + 1)] : variant.title;
      if (!value || /^default title$/i.test(value)) return;
      const line = document.createElement('div');
      line.className = 'beisat-wl-size';
      line.textContent = sizeLabel + ': ' + value;
      const anchor = card.querySelector('[class*="title"]') || card.querySelector('a');
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(line, anchor.nextSibling);
    });
    if (isAr) {
      document.querySelectorAll('.st-popup .st-cancel-button').forEach((b) => { if (b.textContent.trim() === 'Cancel') b.textContent = 'إلغاء'; });
      document.querySelectorAll('.st-popup .st-confirm-button').forEach((b) => { if (b.textContent.trim() === 'Delete') b.textContent = 'حذف'; });
    }
  };
  // Light polling only while a wishlist panel is on screen (no MutationObserver:
  // the app re-renders often and observers have looped before).
  let pollTimer = null;
  const startPolling = () => {
    if (pollTimer) return;
    let idle = 0;
    pollTimer = window.setInterval(() => {
      const open = document.querySelector('.st-wishlist-details_products, st-product-card');
      annotateWishlist();
      idle = open ? 0 : idle + 1;
      if (idle > 20) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    }, 700);
  };
  document.addEventListener('click', (event) => {
    if (event.target.closest && event.target.closest('a[href="#st-wishlist-details-button"], .st-wishlist-button, .header-wishlist-icon, [class*="st-wishlist"]')) startPolling();
  });
  window.addEventListener('load', () => {
    annotateWishlist();
    if (location.pathname.indexOf('wishlist') !== -1) startPolling();
  });

  /* App add-to-cart (bundles etc.) → open mini cart / show the error */
  // Apps that wrap window.fetch put /extensions/ in the stack of EVERY request, so
  // only count it as an app add when no theme frame (theme asset or inline script
  // on this page) made the call — theme adds already show the added notification.
  const page = location.origin + location.pathname;
  const fromApp = () => {
    const frames = String(new Error().stack || '').split('\n').filter((f) => f.indexOf('beisat-app-bridge') === -1);
    const stack = frames.join('\n');
    if (!/\/extensions\//.test(stack)) return false;
    return !frames.some((f) => /\/cdn\/shop\/t\/\d+\/assets\//.test(f) || f.indexOf(page + ':') !== -1 || f.indexOf(page + '?') !== -1);
  };
  const isAdd = (url) => /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const watch = isAdd(url) && fromApp();
    const p = nativeFetch.apply(this, arguments);
    if (!watch) return p;
    return p.then(async (res) => {
      if (res.ok) addedToCart();
      else notify(await errorText(res));
      return res;
    });
  };
  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._beisatAdd = isAdd(url) && fromApp();
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this._beisatAdd) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) addedToCart();
        else {
          try {
            const d = JSON.parse(this.responseText);
            notify(d.description || d.message);
          } catch (e) {}
        }
      });
    }
    return nativeSend.apply(this, arguments);
  };
})();
